import { usePlanUnitsOptions } from 'hooks/usePlanUnitsOptions';

import {
  useFilterOptions,
  useGetOptionLabel
} from 'components/shared/PlanUnitSelector/PlanUnitSelector.hooks';

import { getSelectionKey } from '../PlanningEntityPicker.utils';

import type {
  OrganizationalUnitPerspective,
  PlanningEntityPickerOption
} from '../PlanningEntityPicker.types';

type UseOrganizationalUnitSearchOptionsParams = {
  query: string;
  enabled: boolean;
  perspectives: readonly OrganizationalUnitPerspective[];
  /** The keys of what is picked already: a perspective picked for a plan unit is not offered again. */
  pickedKeys: Set<string>;
};

/**
 * The user's plan units and plan groups matching the typed query, one option each, carrying the
 * perspectives still to pick. The first of them is the one a plain pick of the row uses.
 *
 * Filtered client-side: the plan unit list the app already holds is complete and limited to what the user
 * may see, and the backend search has no plan unit strategy. Matched and labelled as the plan unit field
 * does, so a plan unit reads the same here as there.
 */
export const useOrganizationalUnitSearchOptions = ({
  query,
  enabled,
  perspectives,
  pickedKeys
}: UseOrganizationalUnitSearchOptionsParams): PlanningEntityPickerOption[] => {
  'use memo';

  const organizationalUnits = usePlanUnitsOptions({ showGroups: true });
  const filterOptions = useFilterOptions();
  const getOptionLabel = useGetOptionLabel();

  if (!enabled || !query) return [];

  return filterOptions(organizationalUnits, { inputValue: query }).flatMap(
    (organizationalUnit) => {
      const toSelection = (perspective: OrganizationalUnitPerspective) => ({
        kind: 'ORGANIZATIONAL_UNIT' as const,
        organizationalUnitId: organizationalUnit.uniqId,
        // The name alone: the perspective is rendered in the chip, in the language of the moment.
        label: getOptionLabel(organizationalUnit),
        perspective
      });
      const available = perspectives.filter(
        (perspective) =>
          !pickedKeys.has(getSelectionKey(toSelection(perspective)))
      );
      // Every perspective picked: nothing left to offer.
      if (available.length === 0) return [];

      return [
        {
          kind: 'ORGANIZATIONAL_UNIT' as const,
          key: `ORGANIZATIONAL_UNIT:${organizationalUnit.uniqId}`,
          organizationalUnit,
          selection: toSelection(available[0]),
          perspectives: available
        }
      ];
    }
  );
};
